import { Box, Grid, Paper, Typography, useTheme, Button, FormControl, Select, MenuItem } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LuFactory, LuTruck, LuZap, LuFootprints, LuBox, LuActivity, LuInfo, LuLeaf } from "react-icons/lu";
import { FiCopy, FiLayers, FiTrash2, FiCheckCircle, FiShare2, FiZap } from "react-icons/fi";
import { FaRecycle } from "react-icons/fa";
import { v4 as uuidv4 } from "uuid";
import { useNavigate } from "react-router-dom";
import Right_Foot from "../../assets/logo/right_foot.png";
import ProjectCard from "./components/ProjectCard";
import SegmentedTabs from "../../components/tabs/SegmentedTabs";

import { useAuth } from "../../authentication/AuthProvider";
import { envfactor_types } from "../../components/Envfactor_Types";
import AddButton from "../../components/button/AddButton";
import ColumnVisibilityButton from "../../components/button/ColumnVisibilityButton";
import HideButton from "../../components/button/HideButton";
import RefreshButton from "../../components/button/RefreshButton";
import SaveButton from "../../components/button/SaveButton";
import UnhideButton from "../../components/button/UnhideButton";
import DashboardGlobalFootprint from "../../components/chart/DashboardGlobalFootprint";
import { MainContainer } from "../../components/container/MainContainer";
import { PageTitleContainer } from "../../components/container/PageTitleContainer";
import ResizableBox from "../../components/container/ResizableBox";
import StandardCardContainer from "../../components/container/StandardCardContainer";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";
import ColumnMenu from "../../components/context-menu/ColumnMenu";
import ContextMenu from "../../components/context-menu/ContextMenu";
import DialogDelete from "../../components/dialog/DialogDelete";
import UnhideDialog from "../../components/dialog/UnhideDialog";
import ExportAsXLSXButton from "../../components/export/ExportAsXLSXButton";
import AgGrid, { GridPagination } from "../../components/grid/AgGrid";
import { centerAlignColDef, checkBoxColDef, defaultColDef, floatFormater, leftAlignColDef, rightAlignColDef } from "../../components/grid/columnDefinitions/columnDefinitions";
import { ToggleAll } from "../../components/grid/headers/ToggleAll";
import CheckBox from "../../components/input/CheckBox";
import SearchBox from "../../components/input/SearchBox";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import { Project } from "../../db_access/project";
import "../../style/pages-style/Utilizer.css";
import "../../style/pages-style/projects/ProjectDashboard.css";
import "../../style/pages-style/Organization/organizationDashboard.css";
import routing from "../../routing";
import CardTotalGwp from "../Products/Dashboard/CardTotalGwp";

import CardTotalGwpStage from "../Products/Dashboard/CardTotalGwpStage";

import MetricCard from "../../app_components/MetricCard";
import LifecycleStageCards from "../../app_components/LifecycleStageCards";

import { TableFooterCheckBoxesContainer } from "../../components/container/TableFooterCheckBoxesContainer";
import { TableFooterContainer } from "../../components/container/TableFooterContainer";
import { TableHeaderToolsContainer } from "../../components/container/TableHeaderToolsContainer";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";
import { useHiddenProjects, useInvalidateProjectCache, useProjectsDashboard, useProjectsGwpByEnvType, useProjectsGwpByStage } from "../../utilities/useProjectQueries";
import CardsContainer from "../../components/container/CardsContainer";

const METRIC_CONFIG = [
  { key: "total", title: "Total Carbon Footprint", icon: LuFootprints, accent: "#16a34a", bg: "rgba(22, 163, 74, 0.12)", unit: "t CO₂e" },
  { key: "material", title: "Material Impact", icon: LuBox, accent: "#2563eb", bg: "rgba(37, 99, 235, 0.12)", unit: "t CO₂e" },
  { key: "transport", title: "Transport Emissions", icon: LuTruck, accent: "#d97706", bg: "rgba(217, 119, 6, 0.12)", unit: "t CO₂e" },
  { key: "energy", title: "Energy Impact", icon: LuZap, accent: "#7c3aed", bg: "rgba(124, 58, 237, 0.12)", unit: "t CO₂e" },
];

const STAGE_CONFIG = [
  { key: "a1a3", title: "A1 - A3", subtitle: "Manufacture", icon: LuFactory, accent: "#0891b2", bg: "rgba(8, 145, 178, 0.1)", unit: "t CO₂e" },
  { key: "a4a5", title: "A4 - A5", subtitle: "Installation", icon: LuActivity, accent: "#10b981", bg: "rgba(16, 185, 129, 0.1)", unit: "t CO₂e" },
  { key: "mci", title: "Material Circularity Indicator", subtitle: "Circularity Index", icon: FaRecycle, accent: "#f59e0b", bg: "rgba(245, 158, 11, 0.1)", unit: "Index" },
];

const PROJECT_DEFAULT_COLUMNS = [
  "name",
  "gwp",
  "mci",
  "date_modified"
];

const ProjectDashboard = () => {
  const {
    selectedProjects,
    setSelectedProjects,
    selectedProjectIds,
    setSelectedProjectIds,
    refresh,
    setRefresh,
    columnVisibilities,
    isSaveClicked,
    setIsSaveVisible,
    setIsLoading,
    setIsSaveClicked,
    enqueueAlert,
  } = useContextProvider();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const { t } = useTranslation();

  const { isAdmin } = useAuth();
  const colVisibility_key = "project_dashboard_table_column_visibility";
  const defaultNewProject = { name: "", location: "", year: new Date().getFullYear(), description: "", is_published_template: false };
  const gridRef = useRef(null);
  const dialogRef = useRef(null);
  const gridHeaderContainerRef = useRef();
  const isSavingRef = useRef(false); // Guard to prevent concurrent save() calls
  const [activeSubTab, setActiveSubTab] = useState("Dashboard");
  const [templateView, setTemplateView] = useState("Card View");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [validationSubTab, setValidationSubTab] = useState("Pending");
  const [validationView, setValidationView] = useState("Grid View");
  const [projects, setProjects] = useState([]);
  const [focusedProject, setFocusedProject] = useState();
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [globalView, setGlobalView] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [dashboardView, setDashboardView] = useState("Grid View"); // "Grid View" or "Card View"
  const [projectImageCache, setProjectImageCache] = useState({});
  const projectImageCacheRef = useRef({});

  const resolveProjectImageFromRow = useCallback((project) => {
    const candidates = [
      project?.material_photo,
      project?.photo_url,
      project?.photo,
      project?.image_url,
      project?.image,
      project?.thumbnail,
      project?.project_photo,
      project?.workspace_photo,
      project?.workspace_photo_url,
    ];

    for (const value of candidates) {
      if (!value) continue;
      if (typeof value === "string" && value.trim()) return value;
      if (typeof value === "object") {
        if (typeof value.url === "string" && value.url.trim()) return value.url;
        if (typeof value.secure_url === "string" && value.secure_url.trim()) return value.secure_url;
        if (typeof value.path === "string" && value.path.trim()) return value.path;
      }
    }

    return "";
  }, []);

  const handleCardMenuClick = (event, project) => {
    event.preventDefault();
    event.stopPropagation();
    setLocalSelectedIds([project.id]);
    setSelectedProjects([project]);
    setSelectedProjectIds([project.id]);
    setMenuPosition({ x: event.clientX, y: event.clientY });
    setMenuVisible(true);
  };

  const openProjectWorkspace = useCallback((project) => {
    if (!project?.id) return;
    setLocalSelectedIds([project.id]);
    setSelectedProjects([{ ...project }]);
    setSelectedProjectIds([project.id]);
    navigate(`${routing.sector.projects.workspace}?id=${project.id}`);
  }, [navigate, setSelectedProjects, setSelectedProjectIds]);

  const [columnMenuVisible, setColumnMenuVisible] = useState(false);
  const [actionQueue, setActionQueue] = useState([]);
  const [hiddenProjects, setHiddenProjects] = useState([]);
  const [unhideDialogOpen, setUnhideDialogOpen] = useState(false);

  useEffect(() => {
    projectImageCacheRef.current = projectImageCache;
  }, [projectImageCache]);

  useEffect(() => {
    return () => {
      for (const url of Object.values(projectImageCacheRef.current)) {
        if (typeof url === "string" && url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      }
    };
  }, []);

  // CACHED: Fetch dashboard projects
  const { data: cachedProjects, refetch: refetchProjects } = useProjectsDashboard();
  const { data: cachedHiddenProjects = [] } = useHiddenProjects();
  const { invalidateDashboard } = useInvalidateProjectCache();

  // Sync cached projects to local state (for editing)
  useEffect(() => {
    if (!cachedProjects) return;
    if (actionQueue.length > 0 || isSaveClicked) {
      console.log('[ProjectDashboard] ⏸️ Cache update ignored — save in progress or changes in queue:', { queue: actionQueue.length, saving: isSaveClicked });
      return;
    }
    console.log('[ProjectDashboard] 📡 Data synced from cache, count:', cachedProjects.length);
    setProjects((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(cachedProjects)) return prev;
      return cachedProjects;
    });
    setIsLoading(false);
  }, [cachedProjects, actionQueue.length, isSaveClicked]);

  // Sync hidden projects
  useEffect(() => {
    if (cachedHiddenProjects) {
      setHiddenProjects(cachedHiddenProjects);
    }
  }, [cachedHiddenProjects]);

  useEffect(() => {
    setSelectedTemplateId(null);
  }, [activeSubTab]);

  useEffect(() => {
    let cancelled = false;
    const fetchTemplates = async () => {
      try {
        const res = await Project.get_from_admin();
        if (res.ok && !cancelled) {
          const data = await res.json();
          setTemplates(data || []);
        }
      } catch (err) {
        console.error("Error fetching project templates:", err);
      }
    };
    fetchTemplates();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const selectedTemplate = useMemo(() => {
    if (activeSubTab !== "Template Library" || !selectedTemplateId) return null;
    return templates.find((t) => t.id === selectedTemplateId);
  }, [selectedTemplateId, activeSubTab, templates]);

  const handleUseTemplate = async (template) => {
    try {
      setIsLoading(true);
      const res = await Project.duplicate([template.id]);
      if (res.ok) {
        enqueueAlert({ message: t("Project template created in Dashboard"), severity: "success" });
        invalidateDashboard();
        setRefresh((r) => !r);
        setActiveSubTab("Dashboard");
      } else {
        enqueueAlert({ message: t("Failed to duplicate template"), severity: "error" });
      }
    } catch (e) {
      console.error("Error duplicating project template:", e);
      enqueueAlert({ message: t("Failed to duplicate template"), severity: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const validIds = new Set([...projects.map((p) => p.id), ...templates.map((t) => t.id)]);
    setProjectImageCache((prev) => {
      let changed = false;
      const next = {};

      for (const [id, url] of Object.entries(prev)) {
        if (validIds.has(id)) {
          next[id] = url;
        } else {
          changed = true;
          if (typeof url === "string" && url.startsWith("blob:")) {
            URL.revokeObjectURL(url);
          }
        }
      }

      return changed ? next : prev;
    });
  }, [projects, templates]);

  useEffect(() => {
    let cancelled = false;

    const loadProjectImages = async () => {
      const isCardActive =
        (activeSubTab === "Dashboard" && dashboardView === "Card View") ||
        (activeSubTab === "Template Library" && templateView === "Card View") ||
        (activeSubTab === "Validations" && validationView === "Card View");

      if (!isCardActive) return;

      const itemsToLoad = activeSubTab === "Template Library" ? templates : projects;

      for (const project of itemsToLoad) {
        if (!project?.id) continue;
        if (projectImageCacheRef.current[project.id]) continue;

        const mappedImage = resolveProjectImageFromRow(project);
        if (mappedImage) {
          if (!cancelled) {
            setProjectImageCache((prev) => (prev[project.id] ? prev : { ...prev, [project.id]: mappedImage }));
          }
          continue;
        }

        try {
          const res = await Project.get_photo(project.id);
          if (!res.ok) continue;

          const contentType = res.headers.get("content-type") || "";
          if (contentType.includes("application/json")) continue;

          const blob = await res.blob();
          if (!blob || blob.size === 0) continue;

          const objectUrl = URL.createObjectURL(blob);
          if (cancelled) {
            URL.revokeObjectURL(objectUrl);
            return;
          }

          setProjectImageCache((prev) => {
            if (prev[project.id]) {
              URL.revokeObjectURL(objectUrl);
              return prev;
            }
            return { ...prev, [project.id]: objectUrl };
          });
        } catch (error) {
          console.warn("[ProjectDashboard] Failed to load project image", project.id, error);
        }
      }
    };

    loadProjectImages();

    return () => {
      cancelled = true;
    };
  }, [projects, templates, activeSubTab, dashboardView, templateView, validationView, resolveProjectImageFromRow]);

  // Use LOCAL state for selection - this prevents context re-renders from breaking editing
  const [localSelectedIds, setLocalSelectedIds] = useState([]);

  // Derive selected projects from projects and local IDs
  const derivedSelectedProjects = useMemo(() => {
    if (localSelectedIds.length === 0) return [];
    return projects.filter((p) => localSelectedIds.includes(p.id));
  }, [projects, localSelectedIds]);

  // Keep selectedProjects in sync with updated projects list
  useEffect(() => {
    if (derivedSelectedProjects.length > 0 && projects.length > 0) {
      const updatedSelected = derivedSelectedProjects.map((selected) => {
        const found = projects.find((p) => p.id === selected.id);
        return found ? { ...found } : selected;
      });
      if (JSON.stringify(updatedSelected) !== JSON.stringify(derivedSelectedProjects)) {
        setSelectedProjects(updatedSelected);
      }
    }
  }, [projects, derivedSelectedProjects, setSelectedProjects]);

  // Project IDs for aggregation (exclude unsaved/temporary ones to prevent focus-stealing background refetches)
  const projectIdsForAggregation = useMemo(() => {
    const unsavedIds = new Set(
      actionQueue.filter((a) => a.action === "new").map((a) => a.item.id)
    );
    return projects.filter((p) => !unsavedIds.has(p.id)).map((p) => p.id);
  }, [projects, actionQueue]);

  const { data: cachedGwpByStage = [] } = useProjectsGwpByStage(projectIdsForAggregation);
  const { data: cachedGwpByEnvType = [] } = useProjectsGwpByEnvType(projectIdsForAggregation);

  const gwpCardProps = useMemo(() => {
    if (selectedTemplate) {
      const g = Number(selectedTemplate.gwp || 0);
      return {
        total: g,
        material: g * 0.8,
        transport: g * 0.2,
        energy: 0
      };
    }
    const ids = localSelectedIds.length > 0 ? localSelectedIds : projectIdsForAggregation;
    const temp = cachedGwpByEnvType
      .filter((g) => ids.includes(g.project_id))
      .reduce(
        (acc, r) => {
          const total = Number(r.gwp_sum) || 0;
          const transport = envfactor_types.transport === r.type ? total : 0;
          const energy = envfactor_types.energy_sources === r.type ? total : 0;
          const material = total - transport - energy;
          acc.total += total;
          acc.transport += transport;
          acc.energy += energy;
          acc.material += material;
          return acc;
        },
        { total: 0, transport: 0, energy: 0, material: 0 }
      );
    return {
      total: temp.total || 0,
      material: temp.material || 0,
      transport: temp.transport || 0,
      energy: temp.energy || 0
    };
  }, [selectedTemplate, cachedGwpByEnvType, localSelectedIds, projectIdsForAggregation]);

  const stageMetrics = useMemo(() => {
    if (selectedTemplate) {
      const g = Number(selectedTemplate.gwp || 0);
      return {
        a1a3: g * 0.85,
        a4a5: g * 0.15,
        mci: Number(selectedTemplate.mci || 0)
      };
    }
    const ids = localSelectedIds.length > 0 ? localSelectedIds : projectIdsForAggregation;
    const selectedProds = projects.filter((p) => ids.includes(p.id));

    const temp = cachedGwpByStage
      .filter((g) => ids.includes(g.project_id))
      .reduce(
        (acc, r) => {
          acc.A1_A3 += Number(r.A1_A3) || 0;
          acc.A4_A5 += Number(r.A4_A5) || 0;
          return acc;
        },
        { A1_A3: 0, A4_A5: 0 }
      );

    const mciTotal = selectedProds.reduce((acc, p) => acc + (Number(p.mci) || 0), 0);
    const mciAvg = selectedProds.length > 0 ? mciTotal / selectedProds.length : 0;

    return {
      a1a3: temp.A1_A3 || 0,
      a4a5: temp.A4_A5 || 0,
      mci: mciAvg || 0
    };
  }, [selectedTemplate, cachedGwpByStage, localSelectedIds, projectIdsForAggregation, projects]);

  const anyMetricReachesTons = useMemo(() => {
    return Object.values(gwpCardProps).some((val) => typeof val === "number" && val >= 1000);
  }, [gwpCardProps]);

  const anyStageReachesTons = useMemo(() => {
    return [stageMetrics.a1a3, stageMetrics.a4a5].some((val) => typeof val === "number" && val >= 100);
  }, [stageMetrics]);

  const save = useCallback(async () => {
    // Guard: prevent duplicate save() calls (e.g. caused by React re-renders re-triggering isSaveClicked effect)
    if (isSavingRef.current) {
      console.log('[ProjectDashboard] ⛔ save() called while already saving — skipping to prevent duplicates');
      return;
    }
    isSavingRef.current = true;
    try {
      console.log('[ProjectDashboard] 🚀 save() called with', actionQueue.length, 'actions in queue');
      if (gridRef.current && gridRef.current.api) {
        gridRef.current.api.stopEditing();
      }
      const id_map = {};
      const processedIds = new Set();
      for (const actionItem of actionQueue) {
        const { action, item, ids, items } = actionItem;
        console.log(`[ProjectDashboard] ▶️ Start ${action} at`, new Date().toISOString());
        if (action === "new") {
          const latestItem = projects.find((p) => p.id === item.id) || item;
          const res = await Project.new(latestItem);
          const data = await res.json();
          id_map[item.id] = data.id;
          processedIds.add(item.id);
          // Update local state immediately with the real DB ID
          setProjects((prev) => prev.map((p) => (p.id === item.id ? { ...p, id: data.id } : p)));
        } else if (action === "update") {
          if (processedIds.has(item.id)) continue;
          const updatedItem = { ...item, id: id_map[item.id] || item.id };
          await Project.update(updatedItem);
        } else if (action === "update_bulk") {
          const updatedItems = items.map((item) => ({ ...item, id: id_map[item.id] || item.id }));
          await Project.update_bulk(updatedItems);
        } else if (action === "delete") {
          const mapped_ids = ids.map((id) => id_map[id] || id);
          await Project.delete(mapped_ids);
        } else if (action === "archive") {
          const mapped_ids = ids.map((id) => id_map[id] || id);
          await Project.archive(mapped_ids);
        } else if (action === "collaborate") {
          const mapped_ids = ids.map((id) => id_map[id] || id);
          await Project.collaborate(mapped_ids);
        }
        console.log(`[ProjectDashboard] ✅ End ${action} at`, new Date().toISOString());
      }
      if (actionQueue.length === 0) return;

      // Mirror ProductDashboard: await invalidation then explicitly refetch + sync local state
      await invalidateDashboard();
      const { data: refreshedData } = await refetchProjects();
      if (refreshedData) {
        setProjects(refreshedData);
      }

      setIsLoading(false);
      setIsSaveClicked(false);
      setIsSaveVisible(false);
      setActionQueue([]);
      setRefresh((r) => !r);
      console.log('[ProjectDashboard] 🎉 Save complete, queue cleared and state refreshed');
    } catch (error) {
      console.error('[ProjectDashboard] ❌ Save failed:', error);
      setIsLoading(false);
      setIsSaveClicked(false);
      enqueueAlert({ message: t("Failed to save changes"), severity: "error" });
    } finally {
      isSavingRef.current = false;
    }
  }, [actionQueue, projects, setIsLoading, setIsSaveClicked, setIsSaveVisible, setRefresh, invalidateDashboard, refetchProjects, enqueueAlert, t]);

  // Manual refresh trigger
  const lastRefreshRef = useRef(refresh);
  useEffect(() => {
    if (refresh !== lastRefreshRef.current && refresh === true) {
      invalidateDashboard();
      refetchProjects();
    }
    lastRefreshRef.current = refresh;
  }, [refresh]);

  useEffect(() => {
    if (!focusedProject || !focusedProject.id || !gridRef.current) return;
    setTimeout(() => {
      const node = gridRef.current.api.getRowNode(focusedProject.id);
      if (node) {
        gridRef.current.api.ensureIndexVisible(node.rowIndex, "top");
        gridRef.current.api.setFocusedCell(node.rowIndex, "name");
        node.setSelected(true);
        // Automatically start editing the project name
        gridRef.current.api.startEditingCell({ rowIndex: node.rowIndex, colKey: "name" });
      }
      setFocusedProject();
    }, 50);
  }, [projects, focusedProject]);
  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.setGridOption("quickFilterText", searchText);
    }
  }, [searchText]);

  const deleteProject = (selectedProjects) => {
    const ids = selectedProjects.map((s) => s.id);
    console.log('[ProjectDashboard] 🗑️ DELETE triggered, ids:', ids);
    setActionQueue((q) => [...q, { action: "delete", ids }]);
    setProjects((p) => p.filter((prod) => !ids.includes(prod.id)));
    setIsSaveVisible(true);
    enqueueAlert({ message: t("Project deleted successfully") });
    console.log('[ProjectDashboard] ✅ setIsSaveVisible(true) called after DELETE');
  };
  const newProject = () => {
    const newP = { ...defaultNewProject, id: uuidv4() };
    console.log('[ProjectDashboard] ➕ NEW project triggered, id:', newP.id);
    setProjects((p) => [...p, newP]);
    setActionQueue((q) => [...q, { action: "new", item: newP }]);
    setFocusedProject(newP);
    setIsSaveVisible(true);
    enqueueAlert({ message: t("Project created successfully") });
    console.log('[ProjectDashboard] ✅ setIsSaveVisible(true) called after NEW');
  };
  const archiveProject = (selectedProjects) => {
    const ids = selectedProjects.map((s) => s.id);
    console.log('[ProjectDashboard] 📦 ARCHIVE triggered, ids:', ids);
    setActionQueue((q) => [...q, { action: "archive", ids }]);
    setIsSaveVisible(true);
    enqueueAlert({ message: t("Project archived successfully") });
    console.log('[ProjectDashboard] ✅ setIsSaveVisible(true) called after ARCHIVE');
  };

  const hideProjects = async (selectedProjects) => {
    const ids = selectedProjects.map((s) => s.id);
    try {
      await Project.hide(ids);
      invalidateDashboard();
      setRefresh((prev) => !prev);
    } catch (error) {
      console.error("Error hiding projects:", error);
    }
  };

  const unhideProjects = async (ids) => {
    try {
      await Project.unhide(ids);
      invalidateDashboard();
      setRefresh((prev) => !prev);
    } catch (error) {
      console.error("Error unhiding projects:", error);
    }
  };

  const collaborateProject = (selectedProjects) => {
    const ids = selectedProjects.map((s) => s.id);
    setActionQueue((q) => [...q, { action: "collaborate", ids }]);
    setProjects((p) => p.filter((prod) => !ids.includes(prod.id)));
    setIsSaveVisible(true);
    enqueueAlert({ message: t("Project shared for collaboration") });
  };
  const duplicateProject = async (selectedProjects) => {
    const ids = selectedProjects.map((s) => s.id);
    try {
      await Project.duplicate(ids);
      enqueueAlert({ message: t("Project duplicated successfully"), severity: "success" });
      await invalidateDashboard();
      const { data: refreshedData } = await refetchProjects();
      if (refreshedData) setProjects(refreshedData);
    } catch (error) {
      console.error("[ProjectDashboard] ❌ Duplicate failed:", error);
      enqueueAlert({ message: t("Failed to duplicate project"), severity: "error" });
    }
  };
  const requestValidation = async (selectedProjects) => {
    const ids = selectedProjects.map((s) => s.id);
    try {
      const res = await Project.request_validation(ids);
      if (res && !res.ok) {
        let msg = t("Failed to request validation");
        try {
          const errData = await res.json();
          if (errData.message) msg = errData.message;
        } catch {
          // not json
        }
        enqueueAlert({ message: msg, severity: "error" });
        return;
      }
      enqueueAlert({ message: t("Validation requested successfully"), severity: "success" });
      setRefresh((prev) => !prev);
    } catch (error) {
      console.error("Error requesting validation:", error);
      enqueueAlert({ message: t("Failed to request validation"), severity: "error" });
    }
  };

  const toggleAllPublishedTemplates = useCallback(
    (checked) => {
      setProjects((rows) => rows.map((row) => ({ ...row, is_published_template: checked })));
      setActionQueue((q) => [...q, { action: "update_bulk", items: projects.map((p) => ({ ...p, is_published_template: checked })) }]);
      setIsSaveVisible(true);
    },
    [projects, setIsSaveVisible, setActionQueue, setProjects]
  );
  const onSelectionChanged = useCallback(
    (event) => {
      const selected = event.api.getSelectedRows();
      // Update local state for grid
      setLocalSelectedIds(selected.map((s) => s.id));
      // Update context for workspace navigation
      setSelectedProjects(selected.map((s) => ({ ...s })));
      setSelectedProjectIds(selected.map((s) => s.id));
    },
    [setSelectedProjects, setSelectedProjectIds]
  );

  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
    const container = gridRef.current?.eGridDiv || gridHeaderContainerRef.current?.closest('.MuiPaper-root');
    const xPos = container ? container.getBoundingClientRect().left : e.clientX;
    setMenuPosition({ x: xPos, y: e.clientY });
    setMenuVisible(true);
  }, [gridRef]);
  const handleCloseMenu = () => {
    setMenuVisible(false);
  };
  const openDialog = () => {
    if (dialogRef) {
      dialogRef.current.open();
    }
  };
  const contextMenuOptions = [
    { label: t("Duplicate"), action: duplicateProject, icon: <FiCopy />, needSelectedItems: true },
    { label: t("Intra Company"), action: archiveProject, icon: <FiShare2 />, needSelectedItems: true },
    { label: t("Collaborate"), action: collaborateProject, icon: <FiLayers />, needSelectedItems: true },
    { label: t("Delete"), action: openDialog, icon: <FiTrash2 />, needSelectedItems: true },
    { type: 'divider' },
    { label: t("Request Validation"), action: requestValidation, icon: <FiCheckCircle />, needSelectedItems: true },
  ];

  const colDefs = useMemo(() => {
    const isHidden = (field) => {
      const savedVis = columnVisibilities[colVisibility_key];
      if (savedVis) {
        return !savedVis.includes(field);
      }
      return !PROJECT_DEFAULT_COLUMNS.includes(field);
    };
    const isEditable = (params) => {
      if (activeSubTab === "Template Library") return false;
      const status = params.data?.validation_status;
      return status !== "Pending" && status !== "Validated";
    };
    const onCellValueChanged = (params) => {
      if (activeSubTab === "Template Library") return;
      const { data } = params;
      const now = new Date().toString().split(" GMT")[0];
      const updatedRow = data;
      console.log('[ProjectDashboard] ✏️ CELL EDIT triggered, field:', params.colDef?.field, 'id:', updatedRow.id);
      setProjects((prev) => prev.map((row) => (row.id === updatedRow.id ? { ...updatedRow, date_modified: now } : row)));
      setActionQueue((q) => [...q, { action: "update", item: updatedRow }]);
      setIsSaveVisible(true);
      console.log('[ProjectDashboard] ✅ setIsSaveVisible(true) called after CELL EDIT');
    };
    const onCellClicked = (params) => {
      if (activeSubTab === "Template Library") return;
      if (params.data?.validation_status === "Pending" || params.data?.validation_status === "Validated") return;
      const { colDef } = params;
      const updatedRow = { ...params.data, [colDef.field]: !params.value };
      setProjects((prev) => prev.map((row) => (row.id === updatedRow.id ? updatedRow : row)));
      setActionQueue((q) => [...q, { action: "update", item: updatedRow }]);
      setIsSaveVisible(true);
    };
    return [
      {
        headerName: "Name",
        field: "name",
        cellEditor: "agTextCellEditor",
        cellStyle: {
          fontWeight: 500,
          ...leftAlignColDef.cellStyle,
        },
        ...defaultColDef,
        editable: isEditable,
        onCellValueChanged,
      },
      { headerName: "Location", field: "location", cellEditor: "agTextCellEditor", ...defaultColDef, ...centerAlignColDef, editable: isEditable, onCellValueChanged },
      { headerName: "Description", field: "description", cellEditor: "agTextCellEditor", ...defaultColDef, ...centerAlignColDef, editable: isEditable, onCellValueChanged },
      { headerName: "Year", field: "year", cellEditor: "agTextCellEditor", onCellValueChanged, ...defaultColDef, ...centerAlignColDef, editable: isEditable },
      // { headerName: "Quantity", field: "quantity", ...rightAlignColDef, ...defaultColDef, hide: isHidden("quantity") },
      {
        headerName: "GWP (kg CO₂e)",
        field: "gwp",
        colId: "gwp",
        valueGetter: (params) => {
          if (!params.data) return 0;
          const projectGwpRows = cachedGwpByEnvType.filter((g) => g.project_id === params.data.id);
          if (projectGwpRows.length === 0) return params.data.gwp || 0;
          return projectGwpRows.reduce((sum, r) => sum + (Number(r.gwp_sum) || 0), 0);
        },
        ...rightAlignColDef,
        ...defaultColDef,
        ...floatFormater
      },
      { headerName: "Biogenic GWP (kg CO₂e)", field: "biogenic_gwp", ...rightAlignColDef, ...defaultColDef, ...floatFormater },
      { headerName: "Cost ($)", field: "cost", cellEditor: "agTextCellEditor", onCellValueChanged, ...rightAlignColDef, ...defaultColDef, ...floatFormater, editable: isEditable },
      {
        headerName: "Cost per kg CO₂e ($/kg)",
        field: "cost_per_gwp",
        colId: "cost_per_gwp",
        valueGetter: (params) => {
          if (!params.data) return null;
          const projectGwpRows = cachedGwpByEnvType.filter((g) => g.project_id === params.data.id);
          const liveGwp = projectGwpRows.length > 0
            ? projectGwpRows.reduce((sum, r) => sum + (Number(r.gwp_sum) || 0), 0)
            : Number(params.data.gwp || 0);
          if (liveGwp === 0) return null;
          return Number(params.data.cost || 0) / liveGwp;
        },
        ...rightAlignColDef,
        ...defaultColDef,
        ...floatFormater,
        editable: false
      },
      { headerName: "Circularity Index", field: "mci", ...rightAlignColDef, ...defaultColDef, ...floatFormater, editable: false },
      ...(isAdmin
        ? [
          {
            headerName: "Publish Template",
            field: "is_published_template",
            ...checkBoxColDef,
            minWidth: 110,
            width: 110,
            onCellClicked: (params) => {
              if (activeSubTab === "Template Library") return;
              if (params.data?.validation_status === "Pending" || params.data?.validation_status === "Validated") return;
              onCellClicked(params);
            },
            headerComponent: ToggleAll,
            headerComponentParams: { toggle: toggleAllPublishedTemplates },
            editable: isEditable,
            hide: activeSubTab === "Template Library",
          },
        ]
        : []),
      { headerName: "Validation Status", field: "validation_status", ...defaultColDef, ...leftAlignColDef },
      { headerName: "Date Modified", field: "date_modified", ...defaultColDef, ...leftAlignColDef },
    ].map((col) => ({ ...col, hide: isHidden(col.field) }));
  }, [columnVisibilities, isAdmin, toggleAllPublishedTemplates, setIsSaveVisible, cachedGwpByEnvType, activeSubTab]);
  const defaultCols = PROJECT_DEFAULT_COLUMNS;
  useEffect(() => {
    console.log('[ProjectDashboard] 🔔 isSaveClicked changed:', isSaveClicked, '| actionQueue.length:', actionQueue.length);
    if (isSaveClicked) {
      if (actionQueue.length > 0) {
        console.log('[ProjectDashboard] 🚀 Calling save() with', actionQueue.length, 'actions');
        save();
      } else {
        console.log('[ProjectDashboard] ⚠️ isSaveClicked=true but actionQueue is EMPTY — resetting');
        setIsSaveClicked(false);
        setIsLoading(false);
      }
    }
  }, [isSaveClicked, save]);
  // BUG FIX: Only reset save state on refresh if there are no pending changes
  useEffect(() => {
    if (actionQueue.length === 0) {
      console.log('[ProjectDashboard] 🔄 Refresh fired — resetting save state (no pending changes)');
      setIsSaveVisible(false);
      setIsLoading(false);
      setIsSaveClicked(false);
    } else {
      console.log('[ProjectDashboard] 🔄 Refresh fired — BUT skipping reset, pending actions:', actionQueue.length);
    }
  }, [refresh, setIsLoading, setIsSaveVisible, setIsSaveClicked]);

  const memoizedFooterLeftContent = useMemo(() => (
    <Box sx={{ display: 'flex', alignItems: 'center', ml: -1 }}>
      <CheckBox
        checked={globalView}
        onChange={(e) => setGlobalView(e.target.checked)}
        label="Global View"
      />
    </Box>
  ), [globalView]);

  return (
    <MainContainer>

      <Box sx={{ mb: 4 }}>
        <Box className="eng-flex-grid">
          <Box className="eng-metric-item">
            <MetricCard
              title={activeSubTab === "Template Library" ? t("Templates Available") : (activeSubTab === "Validations" ? t("Validations") : t("Active Projects"))}
              value={
                activeSubTab === "Template Library"
                  ? templates.length
                  : activeSubTab === "Validations"
                  ? projects.filter((p) => {
                      const status = p.validation_status || "Not Validated";
                      return validationSubTab === "Validated"
                        ? (status === "Validated" || status === "approved")
                        : status === validationSubTab;
                    }).length
                  : (selectedProjectIds.length > 0 ? selectedProjectIds.length : projects.length)
              }
              icon={FiLayers}
              accent="#6366f1"
              bg="rgba(99, 102, 241, 0.12)"
              type="chain"
              delay={0.05}
            />
          </Box>
          {METRIC_CONFIG.map((m, i) => {
            const { key: metricKey, title, ...otherProps } = m;
            return (
              <Box className="eng-metric-item" key={metricKey}>
                <MetricCard
                  {...otherProps}
                  title={t(title)}
                  value={gwpCardProps[metricKey] || 0}
                  forceTons={anyMetricReachesTons}
                  delay={i * 0.07}
                />
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography sx={{ fontFamily: "Poppins", fontSize: "20px", fontWeight: 500, color: isDark ? "#fff" : "#0f172a", mb: 2, textAlign: 'left' }}>
          {t("Life Cycle Stages")}
        </Typography>
        <Box className="eng-flex-grid">
          {STAGE_CONFIG.map((s, i) => {
            const { key: stageKey, title, subtitle, ...otherProps } = s;
            return (
              <Box className="eng-metric-item" key={stageKey}>
                <LifecycleStageCards
                  {...otherProps}
                  title={t(title)}
                  subtitle={t(subtitle)}
                  variant="stage"
                  value={stageMetrics[stageKey] || 0}
                  forceTons={anyStageReachesTons}
                  delay={(i + 4) * 0.07}
                />
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* TABS SWITCHER - Located directly below Lifecycle stages at 100% width */}
      <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3 }}>
        <Box sx={{ width: "100%" }}>
          <SegmentedTabs
            options={[
              { label: t("Dashboard"), value: "Dashboard" },
              { label: t("Template Library"), value: "Template Library" },
              { label: t("Validations"), value: "Validations" },
            ]}
            selected={activeSubTab}
            setSelected={setActiveSubTab}
          />
        </Box>
      </Box>

      {/* VIEW 1: DASHBOARD */}
      {activeSubTab === "Dashboard" && (
        <Box sx={{ animation: "org-fadeInUp 0.4s ease", display: "flex", flexDirection: "column" }}>
          <Paper variant="Table Container" sx={{ mt: tok.spacing.pageGap }} className="stagger-load-item stagger-delay-6">
            <TableHeaderContainer>
              <TableTitleContainer>
                <Typography variant="Table Title">
                  {t("Projects")}
                </Typography>
              </TableTitleContainer>
              <TableHeaderToolsContainer ref={gridHeaderContainerRef}>
                <Box sx={{ width: 450 }}>
                  <SearchBox text={searchText} setText={setSearchText} placeholder={t("Search for projects...")} width={450} />
                </Box>

                <ColumnVisibilityButton onClick={() => setColumnMenuVisible((p) => !p)} />
                <AddButton onClick={newProject} />
                <ExportAsXLSXButton data={projects} label="Export as .xlsx" filename="projects.xlsx" />
                <HideButton onClick={() => hideProjects(derivedSelectedProjects)} disabled={derivedSelectedProjects.length === 0} />
                <UnhideButton onClick={() => setUnhideDialogOpen(true)} hiddenCount={hiddenProjects.length} />
                <RefreshButton />

                {/* View Switcher Toggle */}
                <Box sx={{ display: 'flex', border: '1px solid', borderColor: tok.border.divider, borderRadius: '8px', overflow: 'hidden', height: '40px' }}>
                  <Button 
                    onClick={() => setDashboardView("Grid View")}
                    sx={{ 
                      borderRadius: 0, 
                      textTransform: 'none',
                      fontFamily: 'Poppins',
                      fontWeight: 600,
                      fontSize: '13px',
                      px: 2,
                      bgcolor: dashboardView === "Grid View" ? "rgba(101, 157, 51, 0.12)" : "transparent",
                      color: dashboardView === "Grid View" ? "#659d33" : (isDark ? "#94a3b8" : "#64748b"),
                      '&:hover': { bgcolor: "rgba(101, 157, 51, 0.2)" }
                    }}
                  >
                    {t("Grid View")}
                  </Button>
                  <Button 
                    onClick={() => setDashboardView("Card View")}
                    sx={{ 
                      borderRadius: 0, 
                      textTransform: 'none',
                      fontFamily: 'Poppins',
                      fontWeight: 600,
                      fontSize: '13px',
                      px: 2,
                      borderLeft: '1px solid',
                      borderColor: tok.border.divider,
                      bgcolor: dashboardView === "Card View" ? "rgba(101, 157, 51, 0.12)" : "transparent",
                      color: dashboardView === "Card View" ? "#659d33" : (isDark ? "#94a3b8" : "#64748b"),
                      '&:hover': { bgcolor: "rgba(101, 157, 51, 0.2)" }
                    }}
                  >
                    {t("Card View")}
                  </Button>
                </Box>
              </TableHeaderToolsContainer>
            </TableHeaderContainer>
            <Box>
              {dashboardView === "Card View" ? (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: tok.spacing?.sectionGap || 3, p: 3 }}>
                  {projects
                    .filter((p) => (p.name || "").toLowerCase().includes(searchText.toLowerCase()))
                    .map((project) => (
                      <ProjectCard
                        key={project.id}
                        project={project}
                        resolvedImage={projectImageCache[project.id]}
                        isDark={isDark}
                        tok={tok}
                        onClick={() => openProjectWorkspace(project)}
                        onAction={() => openProjectWorkspace(project)}
                        actionLabel={t("Open Workspace")}
                        onMenuClick={handleCardMenuClick}
                      />
                    ))}
                </Box>
              ) : (
                <AgGrid
                  ref={gridRef}
                  columnDefs={colDefs}
                  rowData={projects}
                  onSelectionChanged={onSelectionChanged}
                  handleContextMenu={handleContextMenu}
                  colVisibility_key={colVisibility_key}
                  footerLeftContent={memoizedFooterLeftContent}
                />
              )}
            </Box>
          </Paper>
          {/* VIEW 2: Global View */}
          <Box sx={{ display: globalView ? "block" : "none", pt: 4, pb: 2 }}>
            <DashboardGlobalFootprint
              items={derivedSelectedProjects.length > 0 ? derivedSelectedProjects : projects}
              type="project"
            />
          </Box>
        </Box>
      )}

      {/* VIEW 2: TEMPLATE LIBRARY */}
      {activeSubTab === "Template Library" && (
        <Box sx={{ animation: "org-fadeInUp 0.4s ease", display: "flex", flexDirection: "column", mt: 1 }}>
          <Paper variant="Table Container" sx={{ mt: 1 }}>
            <TableHeaderContainer>
              <TableTitleContainer>
                <Typography variant="Table Title">
                  {t("Template Library")} ({templates.length})
                </Typography>
              </TableTitleContainer>
              <TableHeaderToolsContainer>
                <Box sx={{ width: 450 }}>
                  <SearchBox text={searchText} setText={setSearchText} placeholder={t("Search templates...")} width={450} />
                </Box>
                
                {/* View Switcher Toggle */}
                <Box sx={{ display: 'flex', border: '1px solid', borderColor: tok.border.divider, borderRadius: '8px', overflow: 'hidden', height: '40px' }}>
                  <Button 
                    onClick={() => setTemplateView("Grid View")} 
                    sx={{ 
                      borderRadius: 0, 
                      textTransform: 'none',
                      fontFamily: 'Poppins',
                      fontWeight: 600,
                      fontSize: '13px',
                      px: 2,
                      bgcolor: templateView === "Grid View" ? "rgba(101, 157, 51, 0.12)" : "transparent",
                      color: templateView === "Grid View" ? "#659d33" : (isDark ? "#94a3b8" : "#64748b"),
                      '&:hover': { bgcolor: "rgba(101, 157, 51, 0.2)" }
                    }}
                  >
                    {t("Grid View")}
                  </Button>
                  <Button 
                    onClick={() => setTemplateView("Card View")} 
                    sx={{ 
                      borderRadius: 0, 
                      textTransform: 'none',
                      fontFamily: 'Poppins',
                      fontWeight: 600,
                      fontSize: '13px',
                      px: 2,
                      borderLeft: '1px solid',
                      borderColor: tok.border.divider,
                      bgcolor: templateView === "Card View" ? "rgba(101, 157, 51, 0.12)" : "transparent",
                      color: templateView === "Card View" ? "#659d33" : (isDark ? "#94a3b8" : "#64748b"),
                      '&:hover': { bgcolor: "rgba(101, 157, 51, 0.2)" }
                    }}
                  >
                    {t("Card View")}
                  </Button>
                </Box>
              </TableHeaderToolsContainer>
            </TableHeaderContainer>

            <Box sx={{ position: 'relative', p: templateView === "Card View" ? 3 : 0 }}>
              {templateView === "Card View" ? (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: tok.spacing?.sectionGap || 3 }}>
                  {templates
                    .filter((t) => (t.name || "").toLowerCase().includes(searchText.toLowerCase()))
                    .map((template) => (
                      <ProjectCard
                        key={template.id}
                        project={template}
                        resolvedImage={projectImageCache[template.id]}
                        onAction={handleUseTemplate}
                        actionLabel={t("Use Template")}
                        isTemplate={true}
                        isDark={isDark}
                        tok={tok}
                        isSelected={selectedTemplateId === template.id}
                        onClick={() => setSelectedTemplateId((prev) => (prev === template.id ? null : template.id))}
                      />
                    ))}
                </Box>
              ) : (
                <AgGrid
                  columnDefs={colDefs}
                  rowData={templates.filter((t) => (t.name || "").toLowerCase().includes(searchText.toLowerCase()))}
                  onSelectionChanged={() => {}}
                  handleContextMenu={() => {}}
                  colVisibility_key="project_template_library_column_visibility"
                />
              )}
            </Box>
          </Paper>
        </Box>
      )}

      {/* VIEW 3: VALIDATIONS */}
      {activeSubTab === "Validations" && (
        <Box sx={{ animation: "org-fadeInUp 0.4s ease", display: "flex", flexDirection: "column", mt: 1 }}>
          <Paper variant="Table Container" sx={{ mt: 1 }}>
            <TableHeaderContainer>
              <TableTitleContainer>
                <Typography variant="Table Title">
                  {validationSubTab === "Pending" ? t("Pending Validations") : t("Validated Projects")}
                </Typography>
              </TableTitleContainer>
              <TableHeaderToolsContainer>
                <Box sx={{ width: 350 }}>
                  <SearchBox text={searchText} setText={setSearchText} placeholder={t("Search validations...")} width={350} />
                </Box>
                
                {/* Validation Status Dropdown */}
                <FormControl size="small" sx={{ minWidth: 140, ml: 1.5 }}>
                  <Select
                    value={validationSubTab}
                    onChange={(e) => setValidationSubTab(e.target.value)}
                    sx={{
                      height: '40px',
                      borderRadius: '8px',
                      fontFamily: 'Poppins',
                      fontWeight: 600,
                      fontSize: '13px',
                      color: '#659d33',
                      bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'transparent',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: tok.border.divider,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#659d33',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#659d33',
                      }
                    }}
                  >
                    <MenuItem value="Pending" sx={{ fontFamily: 'Poppins', fontSize: '13px', fontWeight: 500 }}>{t("Pending")}</MenuItem>
                    <MenuItem value="Validated" sx={{ fontFamily: 'Poppins', fontSize: '13px', fontWeight: 500 }}>{t("Validated")}</MenuItem>
                  </Select>
                </FormControl>
                
                {/* View Switcher Toggle */}
                <Box sx={{ display: 'flex', border: '1px solid', borderColor: tok.border.divider, borderRadius: '8px', overflow: 'hidden', ml: 1.5, height: '40px' }}>
                  <Button 
                    onClick={() => setValidationView("Grid View")}
                    sx={{ 
                      borderRadius: 0, 
                      textTransform: 'none',
                      fontFamily: 'Poppins',
                      fontWeight: 600,
                      fontSize: '13px',
                      px: 2,
                      bgcolor: validationView === "Grid View" ? "rgba(101, 157, 51, 0.12)" : "transparent",
                      color: validationView === "Grid View" ? "#659d33" : (isDark ? "#94a3b8" : "#64748b"),
                      '&:hover': { bgcolor: "rgba(101, 157, 51, 0.2)" }
                    }}
                  >
                    {t("Grid View")}
                  </Button>
                  <Button 
                    onClick={() => setValidationView("Card View")}
                    sx={{ 
                      borderRadius: 0, 
                      textTransform: 'none',
                      fontFamily: 'Poppins',
                      fontWeight: 600,
                      fontSize: '13px',
                      px: 2,
                      borderLeft: '1px solid',
                      borderColor: tok.border.divider,
                      bgcolor: validationView === "Card View" ? "rgba(101, 157, 51, 0.12)" : "transparent",
                      color: validationView === "Card View" ? "#659d33" : (isDark ? "#94a3b8" : "#64748b"),
                      '&:hover': { bgcolor: "rgba(101, 157, 51, 0.2)" }
                    }}
                  >
                    {t("Card View")}
                  </Button>
                </Box>
              </TableHeaderToolsContainer>
            </TableHeaderContainer>

            <Box sx={{ position: 'relative', p: validationView === "Card View" ? 3 : 0 }}>
              {validationView === "Grid View" ? (
                <AgGrid
                  columnDefs={colDefs}
                  rowData={projects.filter((p) => {
                    const status = p.validation_status || "Not Validated";
                    const matchesStatus = validationSubTab === "Validated"
                      ? (status === "Validated" || status === "approved")
                      : status === validationSubTab;
                    return matchesStatus && (p.name || "").toLowerCase().includes(searchText.toLowerCase());
                  })}
                  onSelectionChanged={onSelectionChanged}
                  handleContextMenu={handleContextMenu}
                  colVisibility_key="project_validation_column_visibility"
                />
              ) : (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: tok.spacing?.sectionGap || 3 }}>
                  {projects
                    .filter((p) => {
                      const status = p.validation_status || "Not Validated";
                      const matchesStatus = validationSubTab === "Validated"
                        ? (status === "Validated" || status === "approved")
                        : status === validationSubTab;
                      return matchesStatus && (p.name || "").toLowerCase().includes(searchText.toLowerCase());
                    })
                    .map((project) => (
                      <ProjectCard
                        key={project.id}
                        project={project}
                        resolvedImage={projectImageCache[project.id]}
                        onClick={() => openProjectWorkspace(project)}
                        isDark={isDark}
                        tok={tok}
                        onMenuClick={(e) => handleCardMenuClick(e, project)}
                      />
                    ))}
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      )}
      <ColumnMenu
        anchorRef={gridHeaderContainerRef}
        visible={columnMenuVisible}
        onClose={() => setColumnMenuVisible(false)}
        colDefs={colDefs}
        colVisibility_key={colVisibility_key}
        defaultCols={defaultCols}
        gridRef={gridRef}
      />
      <ContextMenu x={menuPosition.x} y={menuPosition.y} visible={menuVisible} onClose={handleCloseMenu} selectedItems={derivedSelectedProjects} options={contextMenuOptions} />
      <DialogDelete selectedItems={derivedSelectedProjects} handleDelete={deleteProject} ref={dialogRef} />
      <UnhideDialog open={unhideDialogOpen} onClose={() => setUnhideDialogOpen(false)} hiddenItems={hiddenProjects} onUnhide={unhideProjects} entityType="Projects" />
      <SaveButton />
    </MainContainer>
  );
};

export default ProjectDashboard;
